import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useEffect } from 'react'

import { PURCHASE_TYPES } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import { fireThirdPartyProductEvents } from '@src/quince-site-common/src/helpers/productHelper'

import BestMarketValue from './best-market-value'
import EarnCreditFestivalTag from './earn-credit-festival-tag'
import FinalSaleChip from './final-sale-chip'
import InlineSavings from './inline-savings'
import MainPriceColumn from './main-price-column'
import OfferPriceValue from './offer-price-value'
import PerUnitOfferRow from './per-unit-offer-row'
import { ProductInfoPriceDSProps, usePriceDisplayState } from './price-display-state'
import * as styles from './product-info-price-ds.styles'
import TraditionalPriceColumn from './traditional-price-column'

export type { ProductInfoPriceDSProps } from './price-display-state'

const priceWrapperStylesToApply = {
  festivalOffer: [styles.priceWrapperOfferColumnCss],
  subscriptionOffer: [styles.priceWrapperOfferColumnCss],
  default: [],
}

// Orchestrator: derives render flags via usePriceDisplayState and delegates each scenario to a sub-component.
function ProductInfoPriceDS(props: ProductInfoPriceDSProps) {
  const {
    product,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    savingPercentage,
    formattedFestivalOfferPrice,
    pricePerServing,
    traditionalPricePerServing,
    festivalPricePerUnit,
    subscriptionOfferPricePerUnit,
    isFinalSaleProduct,
    isB2BFinalSaleProduct,
    purchaseOption,
    updatedDesignForV2,
    isVariantThirdParty,
    selectedOptions,
  } = props

  const {
    isDiscountLabelPresent,
    hasPerUnitPricing,
    perUnitSavingsPercentage,
    showBestMarketValue,
    showSavingPercentage,
    showPerUnitSavingsPercentage,
    unitForPerServing,
    offerStyleKey,
    earnCreditTagFor3PVendor,
    isSubscriptionOffer,
    isSubscribePurchase,
  } = usePriceDisplayState(props)

  const showOfferPrice = isSubscriptionOffer || !!formattedFestivalOfferPrice

  // Mount-only ([] deps) so the 3P events fire exactly once; kept in the orchestrator, not a leaf.
  useEffect(() => {
    if (isVariantThirdParty && product && selectedOptions) {
      fireThirdPartyProductEvents(product, selectedOptions, !!earnCreditTagFor3PVendor, showBestMarketValue)
    }
  }, [])

  return (
    <div>
      <div className={cx(styles.priceWrapperCss, ...priceWrapperStylesToApply[offerStyleKey])}>
        <div
          className={cx(
            showOfferPrice ? styles.priceWrapperMainOfferRowCss : styles.priceWrapperMainCss,
            styles.priceWrapperMainSpaceCss,
          )}
        >
          <MainPriceColumn
            formattedSalePrice={formattedSalePrice}
            formattedTraditionalPrice={formattedTraditionalPrice}
            formattedFestivalOfferPrice={formattedFestivalOfferPrice}
            isSubscriptionOffer={isSubscriptionOffer}
            isB2BFinalSaleProduct={isB2BFinalSaleProduct}
            updatedDesignForV2={updatedDesignForV2}
            hasPerUnitPricing={hasPerUnitPricing}
            unitForPerServing={unitForPerServing}
            pricePerServing={pricePerServing}
            traditionalPricePerServing={traditionalPricePerServing}
            earnCreditTagFor3PVendor={earnCreditTagFor3PVendor}
          />
          {showOfferPrice && (
            <OfferPriceValue
              formattedSubscriptionPrice={formattedSubscriptionPrice}
              formattedFestivalOfferPrice={formattedFestivalOfferPrice}
              isSubscriptionOffer={isSubscriptionOffer}
              offerStyleKey={offerStyleKey}
            />
          )}
          {earnCreditTagFor3PVendor && formattedFestivalOfferPrice && (
            <EarnCreditFestivalTag earnCreditTagFor3PVendor={earnCreditTagFor3PVendor} />
          )}
          {isFinalSaleProduct && !isB2BFinalSaleProduct && (
            <FinalSaleChip variant="standard" isVariantThirdParty={isVariantThirdParty} />
          )}
          {/* Savings render inline inside the main column. */}
          <InlineSavings
            savingPercentage={savingPercentage}
            perUnitSavingsPercentage={perUnitSavingsPercentage}
            showSavingPercentage={showSavingPercentage}
            showPerUnitSavingsPercentage={showPerUnitSavingsPercentage}
            updatedDesignForV2={updatedDesignForV2}
          />

          {showBestMarketValue && !formattedFestivalOfferPrice && <BestMarketValue />}
        </div>

        {formattedTraditionalPrice && isDiscountLabelPresent && !hasPerUnitPricing && !showBestMarketValue && (
          <TraditionalPriceColumn
            formattedTraditionalPrice={formattedTraditionalPrice}
            formattedFestivalOfferPrice={formattedFestivalOfferPrice}
            isSubscriptionOffer={isSubscriptionOffer}
            updatedDesignForV2={updatedDesignForV2}
          />
        )}
        {showBestMarketValue && formattedFestivalOfferPrice && <BestMarketValue />}

        {isB2BFinalSaleProduct && !isFinalSaleProduct && <FinalSaleChip variant="b2b" />}
      </div>

      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {(festivalPricePerUnit || (subscriptionOfferPricePerUnit && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
        traditionalPricePerServing &&
        unitForPerServing && (
          <PerUnitOfferRow
            festivalPricePerUnit={festivalPricePerUnit}
            subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
            isSubscribePurchase={isSubscribePurchase}
            traditionalPricePerServing={traditionalPricePerServing}
            unitForPerServing={unitForPerServing}
          />
        )}
    </div>
  )
}

export default ProductInfoPriceDS
